// ── Content model: use-cases ───────────────────────────────────────────────
// Używany przez /zastosowania (hub) i /zastosowania/:slug (strony szczegółowe).

export type UseCase = {
  slug: string;
  /** Krótki tytuł karty na hubie */
  title: string;
  /** Pełny H1 na stronie szczegółowej */
  h1: string;
  /** Meta description (ok. 150 znaków) */
  metaDescription: string;
  /** Słowa kluczowe opisujące intencję wyszukiwania */
  intentKeywords: string[];
  /** Opis problemu — akapit wstępny */
  problem: string;
  /** Jak Pinveo rozwiązuje ten problem — lista punktów */
  solution: string[];
  /** Konkretne funkcje Pinveo istotne w tym scenariuszu */
  funkcje: string[];
  /** Mini-FAQ do rich snippet */
  faq: { q: string; a: string }[];
};

export const USE_CASES: UseCase[] = [
  {
    slug: 'przeglady-okresowe-budynkow',
    title: 'Przeglądy okresowe budynków',
    h1: 'Przeglądy okresowe budynków — protokół i rzut w jednym miejscu',
    metaDescription:
      'Pinveo przyspiesza obowiązkowe przeglądy techniczne budynków: formularze przeglądu, markery na rzucie, protokół PDF dla zarządcy i nadzoru budowlanego.',
    intentKeywords: [
      'protokół przeglądu budynku',
      'obowiązkowy przegląd techniczny budynku',
      'przegląd techniczny budynku aplikacja',
      'formularz przeglądu rocznego budynku',
      'dokumentacja przeglądu pięcioletniego',
    ],
    problem:
      'Zarządcy i inspektorzy budownictwa muszą cyklicznie dokumentować stan techniczny budynków — wyniki przeglądów trafiają do papierowych protokołów, a zlokalizowanie usterek na konkretnej kondygnacji bywa czasochłonne, zwłaszcza przy wieloobiektowych zasobach.',
    solution: [
      'Tworzysz szablon formularza przeglądu dostosowany do rodzaju i wieku budynku — bez pomocy IT.',
      'Podczas inspekcji wskazujesz usterki bezpośrednio na rzucie kondygnacji (PDF lub obraz), każdy marker otrzymuje numer, kategorię i zdjęcia.',
      'Wypełniony formularz i mapa markerów trafiają do jednego raportu PDF z logo organizacji — gotowego dla zarządcy lub nadzoru budowlanego.',
      'Praca offline działa w całości: markery i zdjęcia zapisują się lokalnie i synchronizują po powrocie zasięgu.',
    ],
    funkcje: [
      'Konfigurowalne formularze przeglądu z sekcjami (roczny / pięcioletni / na żądanie)',
      'Markery na rzutach PDF wielostronicowych — każda kondygnacja osobno',
      'Numeracja markerów: własny format lub automatyczna',
      'Zdjęcia z GPS wbudowane w marker i w raport',
      'Raport PDF brandowany logiem zarządcy lub biura technicznego',
      'Statusy markerów: otwarty / rozwiązany / odroczony',
      'Udostępnienie protokołu zarządcy lub właścicielowi nieruchomości',
      'Eksport Excel z pełną listą usterek do archiwum',
    ],
    faq: [
      {
        q: 'Czy Pinveo obsługuje wielostronicowe rzuty budynku?',
        a: 'Tak. Możesz wgrać wielostronicowy PDF z rzutami wszystkich kondygnacji i umieszczać markery na każdej stronie osobno.',
      },
      {
        q: 'Jak wygląda raport dla zarządcy nieruchomości?',
        a: 'Pinveo generuje raport PDF zawierający tabelę usterek, miniatury rzutu z zaznaczonymi markerami, zdjęcia z GPS i dane organizacji. Raport można pobrać lub udostępnić zarządcy bez zakładania przez niego konta.',
      },
      {
        q: 'Czy inspektor może pracować bez internetu na budowie?',
        a: 'Tak. Pinveo działa na telefonie — dostępna jako aplikacja na Android (iOS w przygotowaniu) lub bezpośrednio z przeglądarki. Markery, zdjęcia i formularze zapisują się lokalnie i synchronizują automatycznie po powrocie do sieci.',
      },
      {
        q: 'Czy można dostosować formularz do przeglądu pięcioletniego?',
        a: 'Tak. Kreator formularzy pozwala tworzyć dowolne szablony z sekcjami, typami pól i logiką warunkową — bez pomocy programisty.',
      },
    ],
  },

  {
    slug: 'odbiory-mieszkan',
    title: 'Odbiory mieszkań od dewelopera',
    h1: 'Protokół odbioru mieszkania — wady na rzucie, raport dla dewelopera i klienta',
    metaDescription:
      'Pinveo usprawnia odbiory mieszkań: wady zaznaczasz na rzucie, dokumentujesz zdjęciami z GPS, generujesz protokół PDF dla dewelopera i nabywcy.',
    intentKeywords: [
      'protokół odbioru mieszkania',
      'odbiór mieszkania od dewelopera aplikacja',
      'snagging lista wad mieszkanie',
      'formularz odbioru lokalu',
      'dokumentacja odbioru nieruchomości',
    ],
    problem:
      'Odbiór mieszkania od dewelopera to kilkadziesiąt pozycji do sprawdzenia w napiętym terminie. Usterki zapisane odręcznie na wydrukowanym rzucie trudno zdigitalizować — klient nie dostaje szybko czytelnego protokołu, a deweloper musi ręcznie przepisywać listę wad.',
    solution: [
      'Inspektor lub nabywca zaznacza wady na interaktywnym rzucie mieszkania bezpośrednio na telefonie lub tablecie.',
      'Do każdej wady dołączane są zdjęcia z GPS i krótki opis — bez osobnego aparat.',
      'Po zakończeniu odbioru generujesz protokół PDF z rzutem, tabelą wad, zdjęciami i danymi stron.',
      'Protokół udostępniasz deweloperowi i nabywcy przez bezpieczny link — bez potrzeby drukowania.',
    ],
    funkcje: [
      'Markery na rzucie mieszkania z numeracją wad',
      'Formularz odbioru z polami: rodzaj wady, lokalizacja, priorytet, termin usunięcia',
      'Zdjęcia wad z GPS wbudowane w marker',
      'Raport PDF protokołu odbioru z logiem biura i danymi stron',
      'Miejsce na podpis stron w protokole PDF',
      'Udostępnienie protokołu nabywcy i deweloperowi przez bezpieczny link',
      'Statusy wad: otwarty → rozwiązany z historią zmian',
      'Eksport Excel z pełną listą usterek do dalszego śledzenia',
    ],
    faq: [
      {
        q: 'Jak nabywca i deweloper podpisują protokół?',
        a: 'Protokół PDF zawiera miejsce na podpisy stron — wystarczy go wydrukować i podpisać odręcznie albo podpisać cyfrowo w zewnętrznym narzędziu. Pinveo nie wystawia własnego podpisu elektronicznego.',
      },
      {
        q: 'Jak deweloper otrzymuje listę usterek?',
        a: 'Możesz udostępnić deweloperowi protokół przez bezpieczny link z opcjonalną datą wygaśnięcia — deweloper widzi usterki i zdjęcia bez zakładania konta w Pinveo.',
      },
      {
        q: 'Czy mogę użyć rzutu z dokumentacji projektowej dewelopera?',
        a: 'Tak. Pinveo obsługuje pliki PDF i obrazy (JPG, PNG, TIFF). Wystarczy wgrać rzut z dokumentacji i od razu można zaznaczać wady na planie.',
      },
      {
        q: 'Ile czasu zajmuje wygenerowanie protokołu?',
        a: 'Protokół PDF jest generowany po zakończeniu odbioru — zazwyczaj w kilkadziesiąt sekund. Duże raporty ze zdjęciami są generowane w tle i nie blokują interfejsu.',
      },
    ],
  },

  {
    slug: 'inwentaryzacja-usterek',
    title: 'Inwentaryzacja usterek (snagging)',
    h1: 'Inwentaryzacja usterek na rzucie — snagging z markerami, zdjęciami GPS i raportem',
    metaDescription:
      'Pinveo to narzędzie do inwentaryzacji usterek (snagging): markery na rzucie PDF, zdjęcia z GPS, statusy i priorytety, raport z mapą markerów dla generalnego wykonawcy.',
    intentKeywords: [
      'inwentaryzacja usterek aplikacja',
      'snagging lista usterek budowa',
      'markery na rzucie usterki',
      'protokół usterek budowlanych',
      'snag list budowa narzędzie',
    ],
    problem:
      'Ekipy wykończeniowe i inżynierowie budowy spędzają wiele godzin na ręcznym wypełnianiu list usterek i lokalizowaniu ich na papierowych rzutach. Brak połączenia markerów z zdjęciami i statusami powoduje nieporozumienia między generalnym wykonawcą a podwykonawcami.',
    solution: [
      'Tworzysz zinwentaryzowaną listę usterek wskazując je bezpośrednio na rzucie — każdy marker to pełny rekord z opisem, priorytetem i zdjęciami.',
      'Priorytety i statusy (otwarty / rozwiązany / odroczony) widoczne są od razu dla całego zespołu.',
      'Raport PDF z mapą markerów i tabelą usterek możesz wysłać generalnemu wykonawcy w kilka minut po obejściu obiektu.',
      'Filtrowanie po statusie, kategorii i osobie odpowiedzialnej skraca czas porannych odpraw.',
    ],
    funkcje: [
      'Markery na rzucie PDF z numeracją alfanumeryczną lub własnym formatem',
      'Statusy usterek: otwarty / rozwiązany / odroczony',
      'Priorytety: niski / normalny / wysoki / pilny',
      'Termin usunięcia usterki i przypisanie do wykonawcy',
      'Zdjęcia z GPS i adnotacje wbudowane w marker',
      'Bulk edit: hurtowa zmiana statusu wielu markerów naraz',
      'Raport PDF z mapą markerów i tabelą usterek',
      'Eksport Excel z pełną listą usterek do Excela',
      'Filtrowanie po statusie, kategorii, autorze i terminie',
    ],
    faq: [
      {
        q: 'Ile usterek można dodać do jednego obiektu?',
        a: 'Nie ma sztywnego limitu liczby markerów na obiekcie. Pinveo radzi sobie z setkami usterek na jednym rzucie, zapewniając filtrowanie i wyszukiwanie.',
      },
      {
        q: 'Czy można przypisać usterkę do konkretnego podwykonawcy?',
        a: 'Tak. Każdy marker możesz przypisać do konkretnego użytkownika z zespołu i ustawić termin usunięcia.',
      },
      {
        q: 'Jak wygląda raport przekazywany generalnemu wykonawcy?',
        a: 'Raport PDF zawiera rzut z naniesionymi markerami, tabelę usterek ze statusami i priorytetami, miniatury zdjęć i dane organizacji. Możesz udostępnić raport przez link z datą wygaśnięcia.',
      },
      {
        q: 'Czy Pinveo działa na budowie bez internetu?',
        a: 'Tak. Pinveo działa na telefonie — dostępna jako aplikacja na Android lub iPhone (z przeglądarki). Nowe usterki i zdjęcia zapisują się offline i synchronizują po powrocie do zasięgu.',
      },
    ],
  },

  {
    slug: 'bhp-audyty',
    title: 'Audyty i kontrole BHP',
    h1: 'Audyty BHP z konfigurowalnymi checklistami — niezgodności, zalecenia i raport pokontrolny',
    metaDescription:
      'Pinveo wspiera audyty i kontrole BHP: konfigurowalne checklisty i formularze, rejestr niezgodności z markerami na rzucie, zalecenia pokontrolne i raport PDF dla kierownictwa.',
    intentKeywords: [
      'audyt BHP aplikacja mobilna',
      'kontrola BHP formularz',
      'rejestr niezgodności BHP',
      'protokół audytu bezpieczeństwa',
      'checklist BHP mobile',
    ],
    problem:
      'Specjaliści BHP prowadzą kontrole na podstawie papierowych lub excelowych checklistów, które nie łączą niezgodności z lokalizacją na obiekcie. Zalecenia pokontrolne są trudne do śledzenia, a zarządzanie ich realizacją wymaga osobnych narzędzi.',
    solution: [
      'Tworzysz dedykowaną checklistę BHP dla każdego rodzaju kontroli — bez programowania, w wizualnym kreatorze formularzy.',
      'Niezgodności zaznaczasz na rzucie lub mapie obiektu jako markery z opisem, priorytetem i zdjęciami.',
      'Raport pokontrolny z listą niezgodności, zaleceniami i terminami trafia do kierownictwa jako PDF w kilka minut po zakończeniu audytu.',
      'Statusy zaleceń (otwarty / rozwiązany) śledzisz do momentu zamknięcia.',
    ],
    funkcje: [
      'Konfigurowalne checklisty BHP z logiką warunkową (pole pojawia się gdy zaznaczysz niezgodność)',
      'Markery niezgodności na rzucie obiektu lub planie sytuacyjnym',
      'Priorytety: niski / normalny / wysoki / pilny z kolorami kategorii',
      'Termin realizacji zalecenia i przypisanie osoby odpowiedzialnej',
      'Raport PDF pokontrolny z logo organizacji i podpisem inspektora',
      'Rejestr niezgodności filtrowany po statusie, kategorii i terminie',
      'Dziennik zmian — kto zmienił status zalecenia i kiedy',
      'Eksport Excel z rejestrem niezgodności do archiwum',
    ],
    faq: [
      {
        q: 'Czy można mieć różne checklisty dla różnych rodzajów kontroli BHP?',
        a: 'Tak. Kreator szablonów pozwala tworzyć dowolną liczbę szablonów formularzy — osobny dla kontroli miesięcznej, kwartalnej, po wypadku itd.',
      },
      {
        q: 'Jak poinformować pracownika odpowiedzialnego za usunięcie niezgodności?',
        a: 'Markerowi możesz przypisać użytkownika z zespołu i ustawić termin. Powiadomienia in-app informują o zmianach statusów i przypisaniach.',
      },
      {
        q: 'Czy raport BHP można dostosować do wymagań firmy?',
        a: 'Tak. Raporty PDF generowane są na podstawie szablonów organizacji — możesz dodać logo, dane firmy i własny układ dokumentu.',
      },
      {
        q: 'Czy Pinveo wspiera kontrole zewnętrzne (PIP, inspekcja pracy)?',
        a: 'Pinveo nie jest certyfikowanym oprogramowaniem do rejestrów urzędowych, ale pozwala przygotować czytelną dokumentację pokontrolną PDF, którą można przekazać inspektorowi.',
      },
    ],
  },

  {
    slug: 'instalacje-geodezja',
    title: 'Dokumentacja instalacji i prac terenowych',
    h1: 'Dokumentacja instalacji i prac terenowych — markery na rzutach i mapach, zdjęcia GPS, praca offline',
    metaDescription:
      'Pinveo umożliwia dokumentację prac instalacyjnych i geodezyjnych: markery na rzutach PDF i mapach, zdjęcia z GPS, pełna praca offline na budowie bez zasięgu.',
    intentKeywords: [
      'dokumentacja instalacji elektrycznych',
      'dokumentacja prac geodezyjnych aplikacja',
      'inwentaryzacja instalacji budynku',
      'markery na mapie GPS offline',
      'dokumentacja powykonawcza instalacji',
    ],
    problem:
      'Brygady instalacyjne i geodeci dokumentują prace na kartce lub w Excelu, a zdjęcia z GPS trafiają do folderu bez kontekstu. Zebranie kompletnej dokumentacji powykonawczej — z lokalizacją elementów na rzucie i zdjęciami — zajmuje godziny po zakończeniu prac.',
    solution: [
      'Każdy element instalacji lub punkt geodezyjny zaznaczasz na rzucie lub mapie jako marker z opisem i zdjęciami GPS.',
      'Praca offline działa w pełni — markery i zdjęcia zapisują się na telefonie i synchronizują po powrocie do zasięgu.',
      'Dokumentacja powykonawcza generowana jest jako raport PDF z mapą markerów, tabelą elementów i zdjęciami z GPS i EXIF.',
      'Udostępniasz dokumentację inwestorowi lub biuru projektu przez bezpieczny link.',
    ],
    funkcje: [
      'Markery na rzutach PDF i na mapach obiektów z GPS',
      'Zdjęcia z automatycznym parsowaniem GPS (EXIF — szerokość, długość, dokładność)',
      'Photo overlay z datą, GPS, adresem i logo organizacji',
      'Pełna praca offline: markery i zdjęcia zapisywane lokalnie na urządzeniu',
      'Automatyczna synchronizacja z ponawianiem po powrocie zasięgu',
      'Galeria zdjęć z klastrowaniem punktów GPS',
      'Raport PDF z mapą markerów i zdjęciami GPS',
      'Eksport ZIP z pełną dokumentacją: PDF + zdjęcia + metadata JSON',
    ],
    faq: [
      {
        q: 'Czy Pinveo działa bez internetu na terenie budowy lub w terenie?',
        a: 'Tak. Pinveo działa na telefonie — jako aplikacja na Android lub bezpośrednio z przeglądarki na iPhone. Wszystkie markery, zdjęcia i formularze zapisują się lokalnie i synchronizują automatycznie gdy pojawi się połączenie.',
      },
      {
        q: 'Jak Pinveo pobiera lokalizację GPS ze zdjęć?',
        a: 'Pinveo automatycznie parsuje dane EXIF ze zdjęć wykonanych telefonem: szerokość i długość geograficzną, dokładność GPS oraz czas wykonania. Dane te są widoczne w markerze i trafiają do raportu PDF oraz do mapy zdjęć.',
      },
      {
        q: 'Czy dokumentację powykonawczą można przekazać inwestorowi?',
        a: 'Tak. Możesz udostępnić projekt inwestorowi lub biuru projektu przez link z opcjonalną datą wygaśnięcia — bez zakładania konta. Inwestor pobiera raporty PDF i eksporty ZIP bezpośrednio z portalu.',
      },
      {
        q: 'Czy można dokumentować instalacje na mapie terenu, a nie tylko na rzucie budynku?',
        a: 'Tak. Pinveo obsługuje zarówno rzuty PDF/obrazy (rzuty kondygnacji, plany instalacji), jak i mapy obiektów z podkładem kafelkowym — możesz wskazywać punkty na terenie działki lub poza budynkiem.',
      },
    ],
  },

  // ── 8 nowych wpisów use-case ──────────────────────────────────────────────────

  {
    slug: 'przeglady-instalacji-elektrycznej',
    title: 'Przeglądy instalacji elektrycznej',
    h1: 'Dokumentacja przeglądu instalacji elektrycznej — protokół, lokalizacja na rzucie, zdjęcia',
    metaDescription:
      'Pinveo przyspiesza dokumentację przeglądu instalacji elektrycznej: formularz, markery na rzucie z lokalizacją usterek, zdjęcia z GPS i raport PDF dla zarządcy. Przegląd co 5 lat.',
    intentKeywords: [
      'dokumentacja przeglądu instalacji elektrycznej',
      'przegląd instalacji elektrycznej co 5 lat',
      'protokół przeglądu elektrycznego budynku',
      'aplikacja do przeglądów instalacji elektrycznej',
      'pomiary elektryczne dokumentacja',
    ],
    problem:
      'Zarządcy budynków są zobowiązani do przeprowadzania okresowych przeglądów instalacji elektrycznej (co najmniej co 5 lat). Dokumentacja usterek i niedociągnięć trafia zazwyczaj do papierowych protokołów bez powiązania z konkretną lokalizacją na rzucie — zebranie spójnego raportu z pomiarów i oględzin jest żmudne i czasochłonne.',
    solution: [
      'Inspektor zaznacza stwierdzone usterki bezpośrednio na rzucie kondygnacji — każdy marker zawiera opis, kategorię i zdjęcia z GPS.',
      'Formularz przeglądu elektrycznego konfigurujesz pod własne potrzeby: sekcje dla rozdzielni, przewodów, gniazd, zabezpieczeń itd.',
      'Raport PDF z rzutem, tabelą usterek i zdjęciami jest gotowy tuż po zakończeniu oględzin.',
      'Praca offline na telefonie: markery i zdjęcia zapisują się lokalnie, synchronizacja następuje automatycznie po powrocie zasięgu.',
    ],
    funkcje: [
      'Konfigurowalne formularze przeglądu z sekcjami dla każdego rodzaju instalacji',
      'Markery na rzucie PDF z lokalizacją usterek i numeracją',
      'Zdjęcia z GPS i datą wbudowane w marker i raport',
      'Statusy usterek: otwarty / rozwiązany / odroczony',
      'Raport PDF brandowany logiem zarządcy lub biura technicznego',
      'Eksport Excel z rejestrem usterek do archiwum',
      'Udostępnienie raportu zarządcy przez bezpieczny link',
      'Praca offline na Androidzie — pełna synchronizacja po powrocie zasięgu',
    ],
    faq: [
      {
        q: 'Jak często należy przeprowadzać przegląd instalacji elektrycznej w budynku?',
        a: 'Zgodnie z przepisami Prawa budowlanego instalację elektryczną należy sprawdzać co najmniej co 5 lat. Pinveo ułatwia dokumentowanie każdego przeglądu: formularze, markery na rzucie i raport PDF są dostępne od razu po zakończeniu oględzin.',
      },
      {
        q: 'Kto może wykonać przegląd instalacji elektrycznej?',
        a: 'Przegląd i pomiary ochronne instalacji elektrycznej musi wykonać osoba z uprawnieniami SEP (Stowarzyszenie Elektryków Polskich) grupy E lub D. Pinveo to narzędzie do dokumentacji terenowej, nie certyfikowany rejestr urzędowy — kontrolę wykonuje osoba z wymaganymi uprawnieniami.',
      },
      {
        q: 'Czy Pinveo zastępuje protokół pomiarów elektrycznych?',
        a: 'Nie. Pinveo wspiera dokumentację fotograficzną i lokalizacyjną oględzin — nie zastępuje protokołu pomiarów sporządzanego przez uprawnionego elektryka (SEP). Oba dokumenty można dołączyć do akt obiektu.',
      },
      {
        q: 'Czy można dołączyć wyniki pomiarów do raportu Pinveo?',
        a: 'Tak. W formularzu możesz dodać pola numeryczne na wyniki pomiarów ochrony przeciwporażeniowej i rezystancji izolacji. Wartości trafiają do tabeli w raporcie PDF.',
      },
      {
        q: 'Jak działa Pinveo bez internetu na terenie obiektu?',
        a: 'Pinveo działa jako aplikacja na Android. Markery, zdjęcia i wypełnione formularze zapisują się lokalnie i synchronizują automatycznie po powrocie do sieci.',
      },
    ],
  },

  {
    slug: 'kontrola-przewodow-kominowych',
    title: 'Kontrola przewodów kominowych',
    h1: 'Dokumentacja kontroli przewodów kominowych i wentylacji — markery, zdjęcia, raport',
    metaDescription:
      'Pinveo dokumentuje kontrolę przewodów kominowych i wentylacji: markery usterek na rzucie budynku, zdjęcia z GPS, raport PDF dla zarządcy. Uzupełnienie wewnętrznej dokumentacji fotograficznej.',
    intentKeywords: [
      'dokumentacja kontroli przewodów kominowych',
      'kontrola kominiarza aplikacja',
      'przegląd przewodów wentylacyjnych dokumentacja',
      'protokół kontroli kominowej zarządca',
      'rejestr przeglądów kominiarskich budynek',
    ],
    problem:
      'Zarządcy nieruchomości zobowiązani są do corocznej kontroli przewodów kominowych i wentylacyjnych. Dokumentacja zdjęciowa i lokalizacja usterek na rzucie (np. pęknięcia, nieszczelności, nieprawidłowy ciąg) są trudne do zebrania i przechowywania w spójny sposób — zwłaszcza przy wielolokalowych budynkach z dziesiątkami kominów.',
    solution: [
      'Mistrz kominiarski lub zarządca zaznacza usterki kominowe bezpośrednio na rzucie kondygnacji jako markery z opisem i zdjęciami.',
      'Każdy marker otrzymuje kategorię (przewód dymowy, spalinowy, wentylacyjny), priorytet i status.',
      'Raport PDF z rzutem i tabelą usterek jest gotowy do archiwum zarządcy.',
      'Dokumentacja fotograficzna z GPS trafia automatycznie do markera — bez ręcznego przenoszenia plików.',
    ],
    funkcje: [
      'Markery na rzucie budynku z kategorią rodzaju przewodu (dymowy / spalinowy / wentylacyjny)',
      'Zdjęcia z GPS i datą wbudowane w marker',
      'Formularz kontroli z sekcjami dla każdego rodzaju przewodu',
      'Statusy usterek i termin usunięcia',
      'Raport PDF dla zarządcy z rzutem, tabelą i zdjęciami',
      'Eksport Excel z rejestrem kontroli do archiwum',
      'Udostępnienie raportu zarządcy lub właścicielowi przez bezpieczny link',
      'Praca offline na Androidzie z automatyczną synchronizacją',
    ],
    faq: [
      {
        q: 'Czy Pinveo zastępuje urzędowy e-protokół kominiarski (CEEB)?',
        a: 'Nie. Od września 2023 r. obowiązuje rządowy elektroniczny protokół kominiarski w systemie CEEB (Centralna Ewidencja Emisyjności Budynków) — Pinveo nie jest systemem CEEB ani rejestrem urzędowym. Pinveo to uzupełniająca dokumentacja wewnętrzna i fotograficzna dla zarządcy nieruchomości.',
      },
      {
        q: 'Kto może przeprowadzić kontrolę przewodów kominowych?',
        a: 'Kontrolę przewodów kominowych może wykonywać wyłącznie mistrz kominiarski lub osoba posiadająca kwalifikacje budowlane w odpowiedniej specjalności. Pinveo to narzędzie do dokumentacji terenowej — kontrolę wykonuje osoba z wymaganymi uprawnieniami.',
      },
      {
        q: 'Jak często przeprowadza się kontrolę kominową?',
        a: 'Przewody dymowe, spalinowe i wentylacyjne należy kontrolować co najmniej raz w roku — jest to obowiązkowy przegląd okresowy wymagany Prawem budowlanym. Pinveo ułatwia archiwizację każdego rocznego przeglądu w jednym miejscu.',
      },
      {
        q: 'Czy Pinveo obsługuje budynki wielorodzinne z wieloma lokalami?',
        a: 'Tak. Możesz wgrać wielostronicowy rzut PDF z planami wszystkich kondygnacji i oznaczać usterki kominowe na każdej z nich. Raport zbiorczy obejmuje wszystkie kondygnacje.',
      },
    ],
  },

  {
    slug: 'przeglady-placow-zabaw',
    title: 'Przeglądy placów zabaw',
    h1: 'Przeglądy placów zabaw — dokumentacja kontroli, markery na planie, raport PDF',
    metaDescription:
      'Pinveo wspiera kontrolę placów zabaw: konfigurowalne checklisty PN-EN 1176, markery usterek na planie obiektu, zdjęcia z GPS i raport PDF dla administratora. Aplikacja mobilna Android.',
    intentKeywords: [
      'kontrola placu zabaw aplikacja',
      'dokumentacja przeglądu placu zabaw',
      'formularz kontroli placu zabaw PN-EN 1176',
      'protokół inspekcji placu zabaw',
      'rejestr usterek urządzeń zabawowych',
    ],
    problem:
      'Administratorzy placów zabaw są zobowiązani do regularnych kontroli stanu technicznego urządzeń. Dokumentowanie usterek (pęknięcia, zużycie, niezgodności z normą PN-EN 1176) na papierze jest żmudne, a przechowywanie rejestrów kontroli w spójny sposób — dla wielu obiektów jednocześnie — bywa kłopotliwe.',
    solution: [
      'Na planie placu zabaw zaznaczasz uszkodzone urządzenia jako markery z opisem, kategorią i zdjęciami.',
      'Formularz kontroli dostosujesz do własnych wymagań: kategorie usterek, priorytety (bezpieczeństwo / estetyka / konserwacja), termin usunięcia.',
      'Raport PDF z planem i tabelą usterek jest gotowy dla administratora lub zleceniodawcy tuż po zakończeniu inspekcji.',
      'Praca offline w terenie: markery i zdjęcia zapisują się na telefonie, synchronizacja po powrocie zasięgu.',
    ],
    funkcje: [
      'Markery urządzeń na planie placu zabaw z kategorią i priorytetem',
      'Zdjęcia z GPS i datą — dokumentacja fotograficzna każdej usterki',
      'Konfigurowalne formularze kontroli (codzienne / miesięczne / roczne)',
      'Statusy usterek: otwarty / rozwiązany / odroczony z historią zmian',
      'Raport PDF z planem, tabelą i zdjęciami dla administratora',
      'Eksport Excel z rejestrem kontroli do archiwum',
      'Udostępnienie raportu zleceniodawcy przez bezpieczny link',
      'Aplikacja mobilna na Android z trybem offline',
    ],
    faq: [
      {
        q: 'Jak często należy kontrolować place zabaw?',
        a: 'Norma PN-EN 1176-7 zaleca trzy poziomy inspekcji: rutynową (codzienną lub tygodniową), funkcjonalną (co 1–3 miesiące) i coroczną. Pinveo pozwala prowadzić oddzielne szablony formularzy dla każdego rodzaju kontroli.',
      },
      {
        q: 'Czy Pinveo zastępuje certyfikowany audyt placu zabaw?',
        a: 'Nie. Certyfikowany audyt bezpieczeństwa (np. przez rzeczoznawcę lub firmę certyfikującą) to oddzielna procedura. Pinveo dokumentuje przeprowadzone kontrole i usterki w formie fotograficzno-lokalizacyjnej — to uzupełnienie, nie zastąpienie wymagań normowych.',
      },
      {
        q: 'Czy mogę oznaczyć urządzenie jako wyłączone z użytkowania?',
        a: 'Tak. Marker może mieć status i kategorię „wyłączono z użytkowania" — informacja trafia do raportu PDF i do rejestru usterek.',
      },
      {
        q: 'Czy Pinveo działa na telefonie bez internetu na placu zabaw?',
        a: 'Tak. Aplikacja na Android zapisuje markery i zdjęcia lokalnie i synchronizuje je automatycznie po powrocie do zasięgu.',
      },
    ],
  },

  {
    slug: 'utrzymanie-ruchu-przeglady',
    title: 'Utrzymanie ruchu i przeglądy techniczne',
    h1: 'Przeglądy techniczne i utrzymanie ruchu — checklisty, usterki na planie hali, raporty',
    metaDescription:
      'Pinveo przyspiesza dokumentację przeglądów technicznych w zakładzie: konfigurowalne checklisty maszyn i urządzeń, markery usterek na planie hali, raporty PDF i Excel dla kierownictwa.',
    intentKeywords: [
      'dokumentacja przeglądów technicznych w zakładzie',
      'utrzymanie ruchu aplikacja mobilna',
      'checklisty przeglądy maszyn',
      'rejestr usterek maszyn plan hali',
      'protokół przeglądu technicznego urządzenia',
    ],
    problem:
      'Działy utrzymania ruchu dokumentują przeglądy maszyn i urządzeń w Excelu lub na papierze. Brakuje szybkiego sposobu na powiązanie usterki z jej lokalizacją na planie hali produkcyjnej, co utrudnia planowanie napraw i śledzenie historii.',
    solution: [
      'Tworzysz checklisty przeglądowe dla każdego rodzaju maszyny lub urządzenia — bez pomocy IT.',
      'Usterki zaznaczasz na planie hali jako markery z opisem, priorytetem i zdjęciami.',
      'Raport PDF z planem, tabelą usterek i historią napraw jest gotowy dla kierownictwa po zakończeniu obchodu.',
      'Eksport Excel umożliwia dalsze analizy i integrację z arkuszami planowania serwisu.',
    ],
    funkcje: [
      'Konfigurowalne checklisty przeglądów maszyn z logiką warunkową',
      'Markery usterek na planie hali produkcyjnej lub technicznej',
      'Priorytety usterek i przypisanie do pracownika serwisu',
      'Termin naprawy i statusy: otwarty / w trakcie / rozwiązany',
      'Raport PDF z planem, tabelą usterek i zdjęciami',
      'Eksport Excel z rejestrem usterek do dalszych analiz',
      'Historia zmian statusów — kto i kiedy zmienił stan usterki',
      'Praca offline na Androidzie z automatyczną synchronizacją',
    ],
    faq: [
      {
        q: 'Czy Pinveo zastępuje system CMMS do zarządzania serwisem?',
        a: 'Pinveo to narzędzie do dokumentacji i inspekcji terenowej — pozwala szybko zebrać checklisty, usterki na planie i raporty. Nie zastępuje dedykowanego systemu CMMS z modułem zleceń serwisowych, harmonogramowania i historii maszyn. Oba narzędzia mogą się uzupełniać.',
      },
      {
        q: 'Jak często przeprowadza się przeglądy techniczne w zakładzie?',
        a: 'Harmonogram przeglądów wynika z wymagań producenta urządzenia, przepisów BHP i decyzji służb UR zakładu. Pinveo pozwala tworzyć oddzielne szablony dla przeglądów dziennych, tygodniowych i kwartalnych.',
      },
      {
        q: 'Czy można przypisać usterkę do konkretnego technika?',
        a: 'Tak. Każdy marker możesz przypisać użytkownikowi z zespołu i ustawić termin naprawy. Powiadomienia in-app informują o przypisaniu i zmianach statusów.',
      },
      {
        q: 'Czy inspektor może pracować na terenie zakładu bez internetu?',
        a: 'Tak. Aplikacja na Android działa w trybie offline — markery, zdjęcia i formularze zapisują się lokalnie i synchronizują automatycznie po powrocie do sieci Wi-Fi lub LTE.',
      },
    ],
  },

  {
    slug: 'odbiory-domow',
    title: 'Odbiory domów jednorodzinnych',
    h1: 'Odbiór domu jednorodzinnego — protokół usterek na rzucie, zdjęcia, raport dla wykonawcy',
    metaDescription:
      'Pinveo usprawnia odbiór domu od wykonawcy: wady na rzucie wszystkich kondygnacji, elewacji i działki, zdjęcia z GPS, protokół PDF dla inwestora i wykonawcy. Szybki raport usterek.',
    intentKeywords: [
      'odbiór domu od wykonawcy protokół',
      'protokół odbioru domu jednorodzinnego',
      'aplikacja do odbioru domu',
      'dokumentacja usterek domu nowego',
      'snagging lista domu jednorodzinnego',
    ],
    problem:
      'Odbiór domu jednorodzinnego to przegląd wielu kondygnacji, elewacji i terenu działki — znacznie więcej elementów niż przy odbiorze mieszkania. Lista usterek zapisywana na kartce trudno łączy się z konkretną lokalizacją na rzucie, a inwestor często nie dostaje czytelnego protokołu na czas.',
    solution: [
      'Zaznaczasz usterki na rzucie każdej kondygnacji, na planie elewacji i na mapie działki — wszystko w jednym projekcie.',
      'Każda wada otrzymuje opis, zdjęcia z GPS, kategorię (wykończenie, instalacje, konstrukcja, elewacja) i priorytet.',
      'Protokół PDF z rzutem, tabelą usterek i zdjęciami jest gotowy dla inwestora i wykonawcy tuż po zakończeniu odbioru.',
      'Udostępniasz protokół przez bezpieczny link — wykonawca widzi usterki i zdjęcia bez zakładania konta.',
    ],
    funkcje: [
      'Markery na rzutach wszystkich kondygnacji, elewacji i planie działki',
      'Kategorie usterek: wykończenie / instalacje / konstrukcja / elewacja / działka',
      'Zdjęcia z GPS i datą wbudowane w każdy marker',
      'Formularz odbioru z polami: rodzaj wady, lokalizacja, priorytet, termin usunięcia',
      'Protokół PDF z logo biura i danymi stron (inwestor / wykonawca)',
      'Statusy usterek z historią zmian',
      'Udostępnienie protokołu wykonawcy przez bezpieczny link bez konta',
      'Eksport Excel z pełną listą usterek',
    ],
    faq: [
      {
        q: 'Czym odbiór domu różni się od odbioru mieszkania w Pinveo?',
        a: 'Dom jednorodzinny obejmuje wiele kondygnacji, elewację ze wszystkich stron i teren działki. W Pinveo wczytujesz oddzielne rzuty dla każdego poziomu, plany elewacji i zdjęcia działki — wszystko łączy się w jeden protokół. Odbiór mieszkania w trybie "odbiory-mieszkan" jest zoptymalizowany pod lokal z jedną kondygnacją.',
      },
      {
        q: 'Ile czasu zajmuje wygenerowanie protokołu odbioru domu?',
        a: 'Raport PDF generowany jest automatycznie po zakończeniu odbioru — zazwyczaj w kilkadziesiąt sekund. Przy dużej liczbie zdjęć raport jest generowany w tle i nie blokuje interfejsu.',
      },
      {
        q: 'Czy wykonawca musi mieć konto w Pinveo, żeby zobaczyć usterki?',
        a: 'Nie. Możesz udostępnić protokół przez bezpieczny link z opcjonalną datą wygaśnięcia — wykonawca widzi usterki, lokalizacje na rzucie i zdjęcia bez logowania.',
      },
      {
        q: 'Czy mogę użyć rzutu z dokumentacji projektowej?',
        a: 'Tak. Pinveo obsługuje pliki PDF i obrazy (JPG, PNG, TIFF). Wystarczy wgrać rzuty z projektu wykonawczego lub architektonicznego.',
      },
      {
        q: 'Czy Pinveo działa bez internetu podczas odbioru na budowie?',
        a: 'Tak. Aplikacja na Android zapisuje markery i zdjęcia lokalnie — synchronizacja następuje automatycznie po powrocie do zasięgu.',
      },
    ],
  },

  {
    slug: 'zarzadzanie-nieruchomosciami',
    title: 'Zarządzanie nieruchomościami — przeglądy',
    h1: 'Przeglądy i dokumentacja techniczna dla zarządców nieruchomości — w jednym miejscu',
    metaDescription:
      'Pinveo to aplikacja dla zarządcy nieruchomości do przeglądów: markery usterek na rzucie, zdjęcia z GPS, formularze i raporty PDF dla wielu budynków. Bez papierowych protokołów.',
    intentKeywords: [
      'aplikacja dla zarządcy nieruchomości przeglądy',
      'dokumentacja techniczna budynku zarządca',
      'rejestr usterek budynku wielorodzinnego',
      'przeglądy techniczne nieruchomości aplikacja',
      'protokoły przeglądów zarządzanie budynkiem',
    ],
    problem:
      'Zarządcy nieruchomości prowadzą przeglądy wielu budynków i lokali, zbierając dokumentację w różnych formatach — papier, Excel, zdjęcia bez kontekstu. Zebranie historii usterek, statusów napraw i protokołów dla konkretnego budynku w jednym miejscu bywa niemal niemożliwe.',
    solution: [
      'Każdy budynek to oddzielny projekt z rzutami kondygnacji — usterki zaznaczasz bezpośrednio na rzucie, nie opisujesz słownie.',
      'Formularze przeglądów konfigururjesz do rodzaju przeglądu: roczny, półroczny, po awarii.',
      'Raport PDF z rzutem, tabelą usterek i zdjęciami GPS jest gotowy dla właściciela nieruchomości w kilka minut.',
      'Historia markerów i statusów usterek daje pełny obraz zasobu technicznego dla każdego budynku.',
    ],
    funkcje: [
      'Wiele budynków i obiektów w jednym panelu zarządcy',
      'Markery usterek na rzutach PDF każdej kondygnacji',
      'Zdjęcia z GPS i datą — historia fotograficzna każdej usterki',
      'Konfigurowalne formularze przeglądów (roczny / sezonowy / po awarii)',
      'Statusy usterek z historią zmian i przypisaniem do wykonawcy',
      'Raport PDF brandowany logiem zarządcy',
      'Eksport Excel z rejestrem usterek dla całego zasobu',
      'Udostępnienie protokołów właścicielom przez bezpieczny link',
    ],
    faq: [
      {
        q: 'Czy Pinveo obsługuje zarządzanie czynszami i finansami nieruchomości?',
        a: 'Nie. Pinveo koncentruje się na dokumentacji technicznej i przeglądach: rzuty, markery, zdjęcia GPS i raporty PDF. Moduły finansowe, czynszowe i księgowe są poza zakresem aplikacji.',
      },
      {
        q: 'Ile budynków można obsługiwać jednocześnie?',
        a: 'Nie ma sztywnego limitu liczby obiektów. Zarządca może prowadzić osobne projekty dla każdego budynku w ramach jednej organizacji.',
      },
      {
        q: 'Jak właściciel nieruchomości otrzymuje protokół przeglądu?',
        a: 'Możesz udostępnić właścicielowi raport przez bezpieczny link z opcjonalną datą wygaśnięcia — właściciel pobiera PDF bez zakładania konta w Pinveo.',
      },
      {
        q: 'Czy aplikacja działa offline podczas inspekcji budynku?',
        a: 'Tak. Pinveo na Androidzie zapisuje markery i zdjęcia lokalnie i synchronizuje je automatycznie po powrocie do sieci.',
      },
    ],
  },

  {
    slug: 'przeglady-elewacji-dachu',
    title: 'Przeglądy elewacji i dachu',
    h1: 'Dokumentacja przeglądu elewacji i dachu — markery uszkodzeń, zdjęcia z GPS, raport',
    metaDescription:
      'Pinveo dokumentuje przegląd dachu i elewacji: markery uszkodzeń na rzucie i planie elewacji, zdjęcia z GPS, raport PDF dla zarządcy. Szybka dokumentacja stanu technicznego budynku.',
    intentKeywords: [
      'przegląd dachu i elewacji dokumentacja',
      'dokumentacja uszkodzeń elewacji aplikacja',
      'protokół przeglądu dachu budynku',
      'inspekcja elewacji markery zdjęcia GPS',
      'raport stanu technicznego dachu',
    ],
    problem:
      'Inspekcja dachu i elewacji to dokumentowanie dziesiątek uszkodzeń (pęknięcia, zawilgocenia, ubytki tynku, uszkodzenia obróbek blacharskich) rozłożonych na całej powierzchni budynku. Lokalizowanie ich na papierowym planie i późniejsze łączenie z zdjęciami jest żmudne i podatne na błędy.',
    solution: [
      'Na planie elewacji lub rzucie dachu zaznaczasz uszkodzenia jako markery z opisem, kategorią i zdjęciami z GPS.',
      'Kategorie uszkodzeń (pęknięcia, zawilgocenia, ubytki, obróbki blacharskie) konfigurujesz w szablonie formularza.',
      'Raport PDF z planem elewacji/dachu, tabelą uszkodzeń i zdjęciami jest gotowy dla zarządcy lub wspólnoty.',
      'Praca offline na Androidzie — dane synchronizują się po powrocie zasięgu.',
    ],
    funkcje: [
      'Markery uszkodzeń na planie elewacji i rzucie dachu',
      'Kategorie uszkodzeń: pęknięcia / zawilgocenia / ubytki / obróbki blacharskie',
      'Zdjęcia z GPS i datą wbudowane w marker i raport',
      'Konfigurowalne formularze przeglądu elewacji i dachu',
      'Statusy uszkodzeń i termin naprawy',
      'Raport PDF dla zarządcy lub wspólnoty mieszkaniowej',
      'Eksport Excel z rejestrem uszkodzeń do archiwum',
      'Praca offline na Androidzie z automatyczną synchronizacją',
    ],
    faq: [
      {
        q: 'Jak często należy przeprowadzać przegląd elewacji i dachu?',
        a: 'Obowiązkowe przeglądy okresowe budynku obejmują kontrolę co najmniej raz w roku, a przegląd pięcioletni uwzględnia m.in. stan techniczny elewacji, dachu i obróbek blacharskich. Pinveo dokumentuje każdy przegląd: formularz, markery na planie i raport PDF.',
      },
      {
        q: 'Czy można wgrać rzut dachu z dokumentacji projektowej?',
        a: 'Tak. Pinveo obsługuje pliki PDF i obrazy (JPG, PNG, TIFF) jako podkład dla markerów — możesz wgrać rzut dachu z projektu budowlanego lub wykonawczego.',
      },
      {
        q: 'Jak wygląda raport dla zarządcy lub wspólnoty mieszkaniowej?',
        a: 'Raport PDF zawiera plan elewacji lub dachu z naniesionymi markerami uszkodzeń, tabelę z opisami i kategoriami, miniatury zdjęć GPS i dane organizacji. Raport możesz udostępnić zarządcy lub wspólnocie przez bezpieczny link.',
      },
      {
        q: 'Czy Pinveo obsługuje budynki wieloelewacyjne?',
        a: 'Tak. Możesz wgrać oddzielne plany dla każdej elewacji (północna, południowa, wschodnia, zachodnia) i rzut dachu — wszystkie w jednym projekcie, z raportami zbiorczymi.',
      },
    ],
  },

  {
    slug: 'inwentaryzacja-obiektow',
    title: 'Inwentaryzacja obiektów na rzucie',
    h1: 'Inwentaryzacja obiektów na rzucie — markery, pomiary, zdjęcia i raport',
    metaDescription:
      'Pinveo umożliwia inwentaryzację budynku: markery elementów na rzucie PDF, pomiary, zdjęcia z GPS i raport PDF/Excel. Dokumentacja aplikacja dla inspektorów i zarządców.',
    intentKeywords: [
      'inwentaryzacja budynku dokumentacja aplikacja',
      'inwentaryzacja obiektów na rzucie',
      'aplikacja do inwentaryzacji elementów budynku',
      'dokumentacja stanu budynku markery rzut',
      'rejestr elementów budynku zdjęcia GPS',
    ],
    problem:
      'Inwentaryzacja techniczna budynku — dokumentowanie urządzeń, wyposażenia, elementów konstrukcji lub instalacji — wymaga precyzyjnego powiązania każdego elementu z jego lokalizacją na rzucie. Robienie tego na kartce lub w Excelu bez podkładu graficznego prowadzi do błędów i długiego czasu weryfikacji w terenie.',
    solution: [
      'Każdy zinwentaryzowany element zaznaczasz jako marker na rzucie kondygnacji — z opisem, kategorią, pomiarami i zdjęciami.',
      'Pola formularza (typ, producent, numer seryjny, rok montażu, stan techniczny) konfigurujesz do własnych potrzeb bez pomocy IT.',
      'Raport PDF z rzutem i tabelą elementów jest gotowy do archiwum lub przekazania zarządcy.',
      'Eksport Excel pozwala dalej przetwarzać dane inwentaryzacyjne w dowolnych narzędziach.',
    ],
    funkcje: [
      'Markery elementów na rzucie PDF z opisem, kategorią i numerem inwentarzowym',
      'Pola pomiarowe w formularzu: wymiary, powierzchnia, liczba sztuk',
      'Zdjęcia z GPS i datą wbudowane w marker i raport',
      'Konfigurowalne formularze dla różnych typów inwentaryzacji',
      'Raport PDF z rzutem i tabelą zinwentaryzowanych elementów',
      'Eksport Excel z pełną listą elementów do dalszego przetwarzania',
      'Numeracja automatyczna lub własny format oznaczenia elementu',
      'Praca offline na Androidzie z automatyczną synchronizacją',
    ],
    faq: [
      {
        q: 'Czym inwentaryzacja obiektów różni się od inwentaryzacji geodezyjna?',
        a: 'Pinveo służy do dokumentacji terenowej na rzutach i planach — opisywania, fotografowania i lokalizowania elementów budynku lub obiektu. Nie zastępuje geodezyjnej inwentaryzacji powykonawczej z pomiarem współrzędnych, która wymaga uprawnień geodezyjnych i jest realizowana przez geodetę. Oba dokumenty mogą uzupełniać akta obiektu.',
      },
      {
        q: 'Czy można przypisać numer inwentarzowy do markera?',
        a: 'Tak. Możesz włączyć automatyczną numerację markerów lub wpisać własny numer inwentarzowy — format konfigurujesz w ustawieniach projektu.',
      },
      {
        q: 'Czy raport inwentaryzacji można wyeksportować do Excela?',
        a: 'Tak. Eksport Excel zawiera pełną tabelę zinwentaryzowanych elementów z opisami, kategoriami, pomiarami i danymi z formularza — gotową do importu w zewnętrznych systemach ewidencji.',
      },
      {
        q: 'Ile elementów można zinwentaryzować na jednym rzucie?',
        a: 'Nie ma sztywnego limitu liczby markerów. Pinveo radzi sobie z setkami elementów na jednym rzucie, zapewniając filtrowanie, wyszukiwanie i paginację w tabeli.',
      },
      {
        q: 'Czy Pinveo działa offline podczas inwentaryzacji w terenie?',
        a: 'Tak. Aplikacja na Android zapisuje markery, zdjęcia i formularze lokalnie i synchronizuje je automatycznie po powrocie do sieci.',
      },
    ],
  },
];
